import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Checkbox, Input, Modal, Tabs } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow } from '../../components/document';
import {
  useKdNesplnene,
  useUpdateKdUkol,
  type KdUkol,
  useUkolyZadane,
  useUkolyMoje,
  useCreateUkol,
  useUpdateUkol,
  useDeleteUkol,
  useSetUkolStav,
  useUploadUkolVystup,
  useDeleteUkolVystup,
  useStavby,
} from '../../api/hooks';
import type { Ukol, UkolPriorita } from '../../api/schemas';
import { getAuthToken } from '../../lib/api';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { Nastenka } from './Nastenka';

const stavMap: Record<string, { tone: 'danger' | 'yellow' | 'success' | 'chip'; label: string }> = {
  novy: { tone: 'danger', label: 'Nový' },
  rozpracovano: { tone: 'yellow', label: 'Rozpracováno' },
  splneno: { tone: 'success', label: 'Splněno' },
  potvrzeno: { tone: 'chip', label: 'Potvrzeno' },
};

/** Úkol je uzavřený (řešitel splnil i potvrzeno zadavatelem) — pro počty „otevřených" a hlídání termínu. */
function jeUzavreny(stav: string): boolean {
  return stav === 'splneno' || stav === 'potvrzeno';
}

const prioMap: Record<UkolPriorita, { tone: 'danger' | 'yellow' | 'neutral'; label: string }> = {
  vysoka: { tone: 'danger', label: 'Vysoká' },
  stredni: { tone: 'yellow', label: 'Střední' },
  nizka: { tone: 'neutral', label: 'Nízká' },
};

function formatTermin(s: string | null): string {
  if (!s) return '—';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}. ${m[2]}. ${m[1]}` : s;
}

function isPoTerminu(termin: string | null): boolean {
  if (!termin) return false;
  // Akceptuj YYYY-MM-DD i DD. MM. RRRR
  const isoMatch = termin.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let target: Date | null = null;
  if (isoMatch) {
    target = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
  } else {
    const m = termin.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
    if (m) target = new Date(`${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`);
  }
  if (!target || Number.isNaN(target.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return target.getTime() < today.getTime();
}

/** Stáhne výstup úkolu z chráněného endpointu (přidá Bearer token). */
async function downloadVystup(ukolId: number, attId: number, filename: string) {
  const token = getAuthToken();
  const res = await fetch(`/api/ukoly/${ukolId}/vystup/${attId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
  if (!res.ok) {
    window.alert('Stažení se nezdařilo.');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const tabs = [
  { id: 'nastenka', label: 'Nástěnka' },
  { id: 'moje', label: 'Mám splnit' },
  { id: 'zadane', label: 'Zadal jsem' },
  { id: 'kd', label: 'Z kontrolních dnů' },
];

export function UkolyPage() {
  const [view, setView] = useState('nastenka');
  const { data: moje = [] } = useUkolyMoje();
  const openMoje = moje.filter((u) => !jeUzavreny(u.stav)).length;

  return (
    <ListPage
      breadcrumb={['Přehled', 'Úkoly']}
      title="Úkoly"
      subtitle="Osobní nástěnka · delegování úkolů · úkoly z kontrolních dnů"
      tools={
        <Tabs
          items={tabs.map((t) => (t.id === 'moje' && openMoje > 0 ? { ...t, label: `Mám splnit (${openMoje})` } : t))}
          value={view}
          onChange={setView}
        />
      }
    >
      {view === 'nastenka' ? <Nastenka /> : view === 'moje' ? <MamSplnit /> : view === 'zadane' ? <ZadalJsem /> : <UkolyZKd />}
    </ListPage>
  );
}

// ─── ZADAL JSEM (zadavatel) ──────────────────────────────────────────────────

function ZadalJsem() {
  const { data: ukoly = [], isLoading, error } = useUkolyZadane();
  const { data: stavby = [] } = useStavby();
  const create = useCreateUkol();
  const update = useUpdateUkol();
  const del = useDeleteUkol();
  const setStav = useSetUkolStav();

  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState<Ukol | null>(null);

  // Formulář nového úkolu
  const [email, setEmail] = useState('');
  const [nazev, setNazev] = useState('');
  const [popis, setPopis] = useState('');
  const [termin, setTermin] = useState('');
  const [priorita, setPriorita] = useState<UkolPriorita>('stredni');
  const [stavbaId, setStavbaId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  function openNew() {
    setEmail(''); setNazev(''); setPopis(''); setTermin(''); setPriorita('stredni'); setStavbaId(''); setErr(null);
    setNewOpen(true);
  }

  async function handleCreate() {
    if (!email.trim() || !nazev.trim()) return;
    setErr(null);
    try {
      await create.mutateAsync({
        resitel_email: email.trim(),
        nazev: nazev.trim(),
        popis: popis.trim(),
        termin: termin || null,
        priorita,
        stavba_id: stavbaId ? Number(stavbaId) : null,
      });
      setNewOpen(false);
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem v aplikaci není.'
          : status === 400 ? 'Úkol nelze přiřadit sám sobě.'
          : 'Vytvoření úkolu se nezdařilo.',
      );
    }
  }

  const columns: ListColumn<Ukol>[] = [
    { key: 'nazev', label: 'Úkol', primary: true, render: (u) => u.nazev || '—' },
    { key: 'resitel', label: 'Řešitel', width: '180px', dim: true, render: (u) => u.resitel_name || u.resitel_email },
    {
      key: 'termin', label: 'Termín', width: '130px', mono: true,
      render: (u) => {
        const overdue = !jeUzavreny(u.stav) && isPoTerminu(u.termin);
        return <span style={{ color: overdue ? 'var(--sd-danger)' : undefined, fontWeight: overdue ? 700 : 400 }}>{formatTermin(u.termin)}{overdue && ' ⚠️'}</span>;
      },
    },
    { key: 'priorita', label: 'Priorita', width: '110px', render: (u) => { const p = prioMap[u.priorita] ?? prioMap.stredni; return <Badge tone={p.tone} pill>{p.label}</Badge>; } },
    {
      key: 'stav', label: 'Stav', width: '150px',
      render: (u) => {
        const m = stavMap[u.stav] ?? { tone: 'yellow' as const, label: u.stav };
        return (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <Badge tone={m.tone} pill dot>{m.label}</Badge>
            {u.stav === 'splneno' && <span title="Čeká na vaše potvrzení" style={{ fontSize: 11 }}>⏳</span>}
          </span>
        );
      },
    },
    {
      key: 'vystup', label: 'Výstup', width: '90px', align: 'right',
      render: (u) => (u.attachments.length > 0 ? `📎 ${u.attachments.length}` : u.vystup_text ? '📝' : '—'),
    },
  ];

  const open = ukoly.filter((u) => !jeUzavreny(u.stav)).length;
  const kPotvrzeni = ukoly.filter((u) => u.stav === 'splneno').length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>
          {isLoading ? 'Načítám…' : error ? 'Chyba načítání' : `${ukoly.length} úkolů · ${open} otevřených${kPotvrzeni > 0 ? ` · ${kPotvrzeni} k potvrzení` : ''}`}
        </div>
        <Button variant="primary" onClick={openNew}>+ Nový úkol</Button>
      </div>

      <ListTable
        columns={columns}
        rows={ukoly}
        rowKey={(u) => u.id}
        onRowClick={setDetail}
        emptyTitle="Žádné zadané úkoly"
        emptyText="Úkoly, které přiřadíte kolegům, se objeví zde."
      />

      {/* Nový úkol */}
      <Modal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        title="Nový úkol"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Zrušit</Button>
            <Button variant="primary" onClick={handleCreate} disabled={create.isPending || !email.trim() || !nazev.trim()}>
              {create.isPending ? 'Zadávám…' : 'Zadat úkol'}
            </Button>
          </>
        }
      >
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Zadání úkolu">
            <DocRow
              k="E-mail řešitele *"
              v={<Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kdo má úkol splnit (musí mít účet)" />}
            />
            <DocRow k="Název úkolu *" value={nazev} onChange={setNazev} placeholder="stručně, co je potřeba" />
            <DocRow k="Popis" multiline value={popis} onChange={setPopis} placeholder="podrobnosti, co a jak…" />
            <DocRow
              k="Termín (do kdy)"
              v={<input type="date" value={termin} onChange={(e) => setTermin(e.target.value)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600 }} aria-label="Termín" />}
            />
            <DocRow
              k="Priorita"
              v={
                <select value={priorita} onChange={(e) => setPriorita(e.target.value as UkolPriorita)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', fontSize: 13 }}>
                  <option value="nizka">Nízká</option>
                  <option value="stredni">Střední</option>
                  <option value="vysoka">Vysoká</option>
                </select>
              }
            />
            <DocRow
              k="Stavba (volitelně)"
              v={
                <select value={stavbaId} onChange={(e) => setStavbaId(e.target.value)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', fontSize: 13, maxWidth: 240 }}>
                  <option value="">— bez vazby —</option>
                  {stavby.map((s) => <option key={s.id} value={s.id}>{s.nazev}</option>)}
                </select>
              }
            />
          </DocBlock>
        </div>
        {err && <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>{err}</div>}
      </Modal>

      {/* Detail zadaného úkolu */}
      <ZadanyDetailModal
        ukol={detail ? ukoly.find((u) => u.id === detail.id) ?? detail : null}
        onClose={() => setDetail(null)}
        stavby={stavby}
        onSave={async (id, payload) => { await update.mutateAsync({ id, payload }); setDetail(null); }}
        onDelete={async (id) => { if (window.confirm('Opravdu smazat tento úkol?')) { await del.mutateAsync(id); setDetail(null); } }}
        onConfirm={async (id) => { await setStav.mutateAsync({ id, stav: 'potvrzeno' }); setDetail(null); }}
        onReturn={async (id) => { if (window.confirm('Vrátit úkol řešiteli k dopracování?')) await setStav.mutateAsync({ id, stav: 'rozpracovano' }); }}
        saving={update.isPending}
        deleting={del.isPending}
        stavPending={setStav.isPending}
      />
    </>
  );
}

function ZadanyDetailModal({
  ukol, onClose, stavby, onSave, onDelete, onConfirm, onReturn, saving, deleting, stavPending,
}: {
  ukol: Ukol | null;
  onClose: () => void;
  stavby: { id: number; nazev: string }[];
  onSave: (id: number, payload: { nazev: string; popis: string; termin: string | null; priorita: UkolPriorita; stavba_id: number | null }) => void;
  onDelete: (id: number) => void;
  onConfirm: (id: number) => void;
  onReturn: (id: number) => void;
  saving: boolean;
  deleting: boolean;
  stavPending: boolean;
}) {
  const [nazev, setNazev] = useState('');
  const [popis, setPopis] = useState('');
  const [termin, setTermin] = useState('');
  const [priorita, setPriorita] = useState<UkolPriorita>('stredni');
  const [stavbaId, setStavbaId] = useState('');
  const [loadedId, setLoadedId] = useState<number | null>(null);

  // Inicializace formuláře při otevření nového úkolu
  if (ukol && ukol.id !== loadedId) {
    setLoadedId(ukol.id);
    setNazev(ukol.nazev);
    setPopis(ukol.popis);
    setTermin(ukol.termin ?? '');
    setPriorita(ukol.priorita);
    setStavbaId(ukol.stavba_id ? String(ukol.stavba_id) : '');
  }

  if (!ukol) return null;
  const m = stavMap[ukol.stav] ?? { tone: 'yellow' as const, label: ukol.stav };

  return (
    <Modal
      open={!!ukol}
      onClose={onClose}
      title={`Úkol: ${ukol.nazev}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Zavřít</Button>
          <Button variant="danger" onClick={() => onDelete(ukol.id)} disabled={deleting}>{deleting ? 'Mažu…' : '🗑️ Smazat'}</Button>
          {ukol.stav === 'splneno' ? (
            <>
              <Button variant="secondary" onClick={() => onReturn(ukol.id)} disabled={stavPending}>↩ Vrátit k dopracování</Button>
              <Button variant="primary" onClick={() => onConfirm(ukol.id)} disabled={stavPending}>{stavPending ? 'Ukládám…' : '✓ Potvrdit splnění'}</Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => onSave(ukol.id, { nazev: nazev.trim(), popis: popis.trim(), termin: termin || null, priorita, stavba_id: stavbaId ? Number(stavbaId) : null })} disabled={saving || !nazev.trim()}>
              {saving ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Badge tone={m.tone} pill dot>{m.label}</Badge>
        <span style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>Řešitel: <strong>{ukol.resitel_name || ukol.resitel_email}</strong></span>
      </div>

      {ukol.stav === 'splneno' && (
        <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, border: '1px solid #f0d999', background: '#fdf6e3', fontSize: 13, color: '#6b5614' }}>
          ⏳ Řešitel označil úkol jako <strong>splněný</strong>. Zkontrolujte výstup níže a buď <strong>potvrďte splnění</strong>, nebo <strong>vraťte k dopracování</strong>.
        </div>
      )}
      {ukol.stav === 'potvrzeno' && (
        <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, border: '1px solid #bfe0c8', background: '#eef7f0', fontSize: 13, color: '#1f5130' }}>
          ✓ Splnění jste <strong>potvrdili</strong>{ukol.potvrzeno_at ? ` ${formatTermin(ukol.potvrzeno_at.slice(0, 10))}` : ''}. Úkol je uzavřený.
        </div>
      )}

      <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
        <DocBlock idx="01" title="Zadání">
          <DocRow k="Název *" value={nazev} onChange={setNazev} />
          <DocRow k="Popis" multiline value={popis} onChange={setPopis} />
          <DocRow k="Termín" v={<input type="date" value={termin} onChange={(e) => setTermin(e.target.value)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13 }} />} />
          <DocRow
            k="Priorita"
            v={
              <select value={priorita} onChange={(e) => setPriorita(e.target.value as UkolPriorita)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13 }}>
                <option value="nizka">Nízká</option>
                <option value="stredni">Střední</option>
                <option value="vysoka">Vysoká</option>
              </select>
            }
          />
          <DocRow
            k="Stavba"
            v={
              <select value={stavbaId} onChange={(e) => setStavbaId(e.target.value)} style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, maxWidth: 240 }}>
                <option value="">— bez vazby —</option>
                {stavby.map((s) => <option key={s.id} value={s.id}>{s.nazev}</option>)}
              </select>
            }
          />
        </DocBlock>
      </div>

      {(ukol.vystup_text || ukol.attachments.length > 0) && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 6, border: '1px solid #bfe0c8', background: '#eef7f0' }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-text-dim)', marginBottom: 6 }}>Výstup od řešitele</div>
          {ukol.vystup_text && <div style={{ fontSize: 13, marginBottom: ukol.attachments.length ? 10 : 0, whiteSpace: 'pre-wrap' }}>{ukol.vystup_text}</div>}
          {ukol.attachments.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0' }}>
              <span style={{ fontSize: 13 }}>📎 {a.filename}</span>
              <Button size="sm" variant="secondary" onClick={() => downloadVystup(ukol.id, a.id, a.filename)}>⬇ Stáhnout</Button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── MÁM SPLNIT (řešitel) ─────────────────────────────────────────────────────

function MamSplnit() {
  const { data: ukoly = [], isLoading, error } = useUkolyMoje();
  const setStav = useSetUkolStav();
  const upload = useUploadUkolVystup();
  const delVystup = useDeleteUkolVystup();

  const [detail, setDetail] = useState<Ukol | null>(null);
  const [vystupText, setVystupText] = useState('');
  const [loadedId, setLoadedId] = useState<number | null>(null);

  // Vždy pracuj s čerstvými daty (po invalidaci) — najdi aktuální verzi otevřeného úkolu
  const current = detail ? ukoly.find((u) => u.id === detail.id) ?? detail : null;
  if (current && current.id !== loadedId) {
    setLoadedId(current.id);
    setVystupText(current.vystup_text);
  }

  const columns: ListColumn<Ukol>[] = [
    {
      key: 'done', label: '', width: '40px',
      render: (u) => (
        <Checkbox
          checked={jeUzavreny(u.stav)}
          disabled={u.stav === 'potvrzeno'}
          onChange={() => { if (u.stav !== 'potvrzeno') setStav.mutate({ id: u.id, stav: u.stav === 'splneno' ? 'rozpracovano' : 'splneno' }); }}
        />
      ),
    },
    { key: 'nazev', label: 'Úkol', primary: true, render: (u) => <span style={{ textDecoration: jeUzavreny(u.stav) ? 'line-through' : 'none', color: jeUzavreny(u.stav) ? 'var(--sd-text-dimmer)' : undefined }}>{u.nazev || '—'}</span> },
    { key: 'zadavatel', label: 'Zadal', width: '180px', dim: true, render: (u) => u.zadavatel_name || u.zadavatel_email },
    {
      key: 'termin', label: 'Termín', width: '130px', mono: true,
      render: (u) => { const overdue = !jeUzavreny(u.stav) && isPoTerminu(u.termin); return <span style={{ color: overdue ? 'var(--sd-danger)' : undefined, fontWeight: overdue ? 700 : 400 }}>{formatTermin(u.termin)}{overdue && ' ⚠️'}</span>; },
    },
    { key: 'priorita', label: 'Priorita', width: '110px', render: (u) => { const p = prioMap[u.priorita] ?? prioMap.stredni; return <Badge tone={p.tone} pill>{p.label}</Badge>; } },
    { key: 'stav', label: 'Stav', width: '150px', render: (u) => { const m = stavMap[u.stav] ?? { tone: 'yellow' as const, label: u.stav }; return <Badge tone={m.tone} pill dot>{m.label}</Badge>; } },
  ];

  const open = ukoly.filter((u) => !jeUzavreny(u.stav)).length;
  const overdue = ukoly.filter((u) => !jeUzavreny(u.stav) && isPoTerminu(u.termin)).length;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !current) return;
    await upload.mutateAsync({ id: current.id, file });
    e.target.value = '';
  }

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', marginBottom: 12 }}>
        {isLoading ? 'Načítám…' : error ? 'Chyba načítání' : `${open} otevřených${overdue > 0 ? ` · ${overdue} po termínu` : ''}`}
      </div>

      <ListTable
        columns={columns}
        rows={ukoly}
        rowKey={(u) => u.id}
        onRowClick={setDetail}
        emptyTitle="Žádné úkoly k splnění"
        emptyText="Úkoly, které vám někdo přiřadí, se objeví zde."
      />

      <Modal
        open={!!current}
        onClose={() => setDetail(null)}
        title={current ? `Úkol: ${current.nazev}` : 'Úkol'}
        size="md"
        footer={
          current && (
            <>
              <Button variant="ghost" onClick={() => setDetail(null)}>Zavřít</Button>
              {current.stav !== 'potvrzeno' && (
                <>
                  {current.stav === 'novy' && (
                    <Button variant="secondary" onClick={() => setStav.mutate({ id: current.id, stav: 'rozpracovano' })} disabled={setStav.isPending}>▶ Začít</Button>
                  )}
                  {current.stav === 'splneno' ? (
                    <Button variant="secondary" onClick={() => setStav.mutate({ id: current.id, stav: 'rozpracovano' })} disabled={setStav.isPending}>Vrátit rozpracované</Button>
                  ) : (
                    <Button variant="primary" onClick={() => setStav.mutate({ id: current.id, stav: 'splneno', vystupText: vystupText.trim() })} disabled={setStav.isPending}>{setStav.isPending ? 'Ukládám…' : '✓ Splněno'}</Button>
                  )}
                </>
              )}
            </>
          )
        }
      >
        {current && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <Badge tone={(stavMap[current.stav] ?? { tone: 'yellow' as const }).tone} pill dot>{(stavMap[current.stav] ?? { label: current.stav }).label}</Badge>
              <span style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>Zadal: <strong>{current.zadavatel_name || current.zadavatel_email}</strong></span>
              {current.termin && <span style={{ fontSize: 13, color: isPoTerminu(current.termin) && !jeUzavreny(current.stav) ? 'var(--sd-danger)' : 'var(--sd-text-dim)' }}>Termín: <strong>{formatTermin(current.termin)}</strong></span>}
            </div>

            {current.stav === 'splneno' && (
              <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, border: '1px solid #f0d999', background: '#fdf6e3', fontSize: 13, color: '#6b5614' }}>
                ⏳ Označeno jako <strong>splněné</strong> — čeká na potvrzení zadavatele.
              </div>
            )}
            {current.stav === 'potvrzeno' && (
              <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, border: '1px solid #bfe0c8', background: '#eef7f0', fontSize: 13, color: '#1f5130' }}>
                ✓ Zadavatel <strong>potvrdil splnění</strong>. Úkol je uzavřený.
              </div>
            )}

            {current.popis && (
              <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden', marginBottom: 14 }}>
                <DocBlock idx="01" title="Zadání">
                  <DocRow k="Popis" v={<span style={{ whiteSpace: 'pre-wrap' }}>{current.popis}</span>} />
                  {current.stavba_nazev && <DocRow k="Stavba" v={current.stavba_nazev} />}
                </DocBlock>
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-text-dim)', display: 'block', marginBottom: 4 }}>Zpráva k splnění (výstup)</label>
            <textarea
              value={vystupText}
              onChange={(e) => setVystupText(e.target.value)}
              readOnly={current.stav === 'potvrzeno'}
              placeholder="napište, co jste udělali / shrnutí výstupu…"
              rows={3}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: current.stav === 'potvrzeno' ? 'var(--sd-w-f6f5f0, #f6f5f0)' : '#fff' }}
            />

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sd-text-dim)', marginBottom: 6 }}>Nahrané výstupy</div>
              {current.attachments.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)', marginBottom: 8 }}>Zatím nic nenahráno.</div>}
              {current.attachments.map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0' }}>
                  <span style={{ fontSize: 13 }}>📎 {a.filename}</span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="secondary" onClick={() => downloadVystup(current.id, a.id, a.filename)}>⬇</Button>
                    {current.stav !== 'potvrzeno' && (
                      <Button size="sm" variant="ghost" onClick={() => delVystup.mutate({ id: current.id, attId: a.id })} disabled={delVystup.isPending}>Smazat</Button>
                    )}
                  </span>
                </div>
              ))}
              {current.stav !== 'potvrzeno' && (
                <label style={{ display: 'inline-block', marginTop: 8, cursor: 'pointer' }}>
                  <span style={{ display: 'inline-block', padding: '6px 12px', border: '1px solid var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))', borderRadius: 4, fontSize: 13, background: '#fff' }}>
                    {upload.isPending ? 'Nahrávám…' : '+ Nahrát výstup (PDF, dokument…)'}
                  </span>
                  <input type="file" onChange={handleFile} disabled={upload.isPending} style={{ display: 'none' }} />
                </label>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

// ─── Z KONTROLNÍCH DNŮ (původní agregace KdUkol) ─────────────────────────────

function UkolyZKd() {
  const navigate = useNavigate();
  const { activeStavba } = useActiveStavba();
  const stavbaId = activeStavba?.id ?? null;

  const { data: ukoly = [], isLoading, error } = useKdNesplnene(stavbaId);
  const update = useUpdateKdUkol(stavbaId);

  async function toggleDone(u: KdUkol) {
    const next = u.stav === 'splneno' ? 'novy' : 'splneno';
    await update.mutateAsync({ kdId: u.kd_id, ukolId: u.id, stav: next });
  }

  async function setRozpracovano(u: KdUkol) {
    await update.mutateAsync({ kdId: u.kd_id, ukolId: u.id, stav: 'rozpracovano' });
  }

  if (!activeStavba) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
        Nejdřív vyberte stavbu v sidebaru. Úkoly z kontrolních dnů se sbírají per stavba.
      </div>
    );
  }

  const columns: ListColumn<KdUkol>[] = [
    {
      key: 'done', label: '', width: '40px',
      render: (u) => <Checkbox checked={u.stav === 'splneno'} onChange={() => toggleDone(u)} />,
    },
    {
      key: 'popis', label: 'Úkol', primary: true,
      render: (u) => (
        <span
          onClick={() => navigate(`/app/kontrolni-dny/${u.kd_id}`)}
          style={{ textDecoration: u.stav === 'splneno' ? 'line-through' : 'none', color: u.stav === 'splneno' ? 'var(--sd-text-dimmer)' : undefined, cursor: 'pointer' }}
        >
          {u.popis || '—'}
        </span>
      ),
    },
    { key: 'typ', label: 'Typ', width: '120px', dim: true, render: (u) => u.typ || '—' },
    { key: 'kd', label: 'KD', width: '90px', mono: true, dim: true, render: (u) => `KD-${u.kd_id}` },
    { key: 'odpovedny', label: 'Řešitel', width: '160px', dim: true, render: (u) => u.odpovedny || '—' },
    {
      key: 'termin', label: 'Termín', width: '140px', mono: true,
      render: (u) => {
        const overdue = u.stav !== 'splneno' && isPoTerminu(u.termin);
        return <span style={{ color: overdue ? 'var(--sd-danger)' : undefined, fontWeight: overdue ? 700 : 400 }}>{formatTermin(u.termin)}{overdue && ' ⚠️'}</span>;
      },
    },
    {
      key: 'stav', label: 'Stav', width: '160px',
      render: (u) => {
        const m = stavMap[u.stav] ?? { tone: 'yellow' as const, label: u.stav };
        return (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <Badge tone={m.tone} pill dot>{m.label}</Badge>
            {u.stav === 'novy' && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setRozpracovano(u); }} style={{ background: 'transparent', border: '1px solid var(--sd-border)', borderRadius: 4, padding: '2px 6px', fontSize: 10.5, cursor: 'pointer', color: 'var(--sd-text-dim)' }} title="Označit jako rozpracované">▶</button>
            )}
          </span>
        );
      },
    },
  ];

  const open = ukoly.filter((u) => u.stav !== 'splneno').length;
  const overdue = ukoly.filter((u) => u.stav !== 'splneno' && isPoTerminu(u.termin)).length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>
          {isLoading ? 'Načítám…' : error ? 'Chyba' : `${open} otevřených${overdue > 0 ? ` · ${overdue} po termínu` : ''}`}
        </div>
        <Button variant="secondary" onClick={() => navigate('/app/kontrolni-dny')}>Otevřít KD seznam</Button>
      </div>
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={ukoly}
          rowKey={(u) => u.id}
          emptyTitle="Žádné nesplněné úkoly"
          emptyText="Otevřené úkoly z kontrolních dnů se objeví zde."
        />
      )}
    </>
  );
}
